import { dryingTechnologyKind } from "@/lib/dryerTechnology";

export type PartTechnology = "all" | "heat_pump" | "condenser" | "vented";

export interface TechItem {
  id: string;
  value: number;
  technology?: PartTechnology | null;
}

export interface TechnologyGrouping<T extends TechItem> {
  /** True when the appliance is a (washer) dryer without a technology and some items depend on it. */
  unclear: boolean;
  common: T[];
  uncertain: T[];
  /** Sum of all common items' value (before resale %). */
  commonValue: number;
  /** Cheapest / most expensive single technology set (before resale %). */
  minSetValue: number;
  maxSetValue: number;
}

/**
 * Splits harvest items into technology-independent and technology-dependent
 * groups. Only applies to dryers and washer dryers without a drying
 * technology; everything else is one group with a single total.
 */
export function groupByTechnology<T extends TechItem>(
  items: T[],
  category: string | null | undefined,
  dryerTechnology: string | null | undefined,
  applianceClass?: string | null,
): TechnologyGrouping<T> {
  const sum = (xs: T[]) => xs.reduce((s, x) => s + (x.value || 0), 0);
  const applies = !!dryingTechnologyKind(category, applianceClass) && !dryerTechnology;
  const uncertain = applies ? items.filter((i) => i.technology && i.technology !== "all") : [];
  if (uncertain.length === 0) {
    const v = sum(items);
    return { unclear: false, common: items, uncertain: [], commonValue: v, minSetValue: 0, maxSetValue: 0 };
  }
  const common = items.filter((i) => !uncertain.includes(i));
  const sets = new Map<string, number>();
  for (const i of uncertain) sets.set(i.technology!, (sets.get(i.technology!) ?? 0) + (i.value || 0));
  const values = [...sets.values()];
  return {
    unclear: true,
    common,
    uncertain,
    commonValue: sum(common),
    minSetValue: Math.min(...values),
    maxSetValue: Math.max(...values),
  };
}

/** Total recovery value as [min, max] at the given resale fraction (equal when not unclear). */
export function totalRecoveryRange(g: TechnologyGrouping<TechItem>, pct: number): [number, number] {
  return [(g.commonValue + g.minSetValue) * pct, (g.commonValue + g.maxSetValue) * pct];
}

/** Removes technology-uncertain ids from a selection (they must start unticked while unclear). */
export function withoutUncertain(ids: string[], uncertainIds: ReadonlySet<string>): string[] {
  return ids.filter((id) => !uncertainIds.has(id));
}
